import { animated } from '@react-spring/web';
import classNames from 'classnames';
import { type CSSProperties, type MouseEvent } from 'react';

import { type TimelineEntry } from '../../lib/timeline';

import { IdleContextProvider } from '../../contexts/IdleContext';
import PageRoutes, { type PageRoutesProps } from '../PageRoutes';
import BasicButton from '../buttons/BasicButton';

import styles from '../../styles/layouts/timeline-layout.module.css';

interface TimelinePageProps {
    entry: TimelineEntry | null;
    index: number;
    style: CSSProperties | Record<string, unknown>;
    current?: boolean;
    next?: boolean;
    active?: boolean;
    transformed?: boolean;
    scroll?: number;
    transitioning?: boolean;
    zoomedIn?: boolean;
    zoomingIn?: boolean;
    zoomingOut?: boolean;
    paused?: boolean;
    disabled?: boolean;
    onEnter?: (() => void) | null;
    onLeave?: (() => void) | null;
    onNavigate?: PageRoutesProps['onNavigate'];
    onClickNeighbor?: ((e: MouseEvent, index: number) => void) | null;
}

// One page of the timeline: the current page, a neighbour, or the placeholder of the next
// page (`entry` is null). Pages outside the visible range stay empty.
function TimelinePage({
    entry,
    index,
    style,
    current = false,
    next = false,
    active = false,
    transformed = false,
    scroll = 0,
    transitioning = false,
    zoomedIn = false,
    zoomingIn = false,
    zoomingOut = false,
    paused = false,
    disabled = false,
    onEnter = null,
    onLeave = null,
    onNavigate = null,
    onClickNeighbor = null,
}: TimelinePageProps) {
    const url = entry !== null ? entry.url : null;
    return (
        <animated.div
            className={classNames([
                styles.pageContainer,
                {
                    [styles.isEmpty]: url === null || !active,
                    [styles.neighbor]: !current,
                    [styles.current]: current,
                    [styles.notScrolled]: transformed && url !== null && scroll === 0,
                    [styles.transitioning]: transitioning,
                    [styles.entering]: current && !zoomedIn && zoomingIn,
                    [styles.leaving]: current && zoomedIn && zoomingOut,
                    [styles.entered]: current && zoomedIn,
                },
            ])}
            data-transitioning={transitioning ? true : null}
            data-active={active ? 'true' : 'false'}
            style={style}
        >
            <div
                className={styles.pageSize}
                aria-disabled={!current ? 'true' : null}
                inert={!current && !next}
                style={
                    transformed && url !== null ? { transform: `translateY(-${scroll}px)` } : null
                }
            >
                {url !== null && active ? (
                    <IdleContextProvider idle={!current && !next}>
                        <PageRoutes
                            location={url}
                            pageId={entry.id}
                            onNavigate={onNavigate}
                            transitioning={transitioning}
                            current={current}
                            next={next}
                            paused={paused}
                            entered={current && zoomedIn}
                            entering={current && zoomingIn}
                            leaving={current && zoomingOut}
                            disabled={disabled}
                            onEnter={onEnter}
                            onLeave={onLeave}
                            className={styles.page}
                            contentClassName={styles.pageContent}
                        />
                    </IdleContextProvider>
                ) : null}
                {url === null && active ? <div className={styles.placeholder} /> : null}
            </div>
            {!current ? (
                <BasicButton
                    href={url}
                    className={styles.pageLink}
                    clickRef="timeline_next"
                    onClick={
                        onClickNeighbor !== null ? (e) => onClickNeighbor(e, index) : undefined
                    }
                />
            ) : null}
        </animated.div>
    );
}

export default TimelinePage;
